import type { Command } from 'commander';
import { mkdirSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import { fetchResumeList } from './list';
import { fetchResumeAggregate } from './view';
import type { PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';

interface ExportFlags {
	out?: string;
}

/**
 * server 回傳的 `enc_id` 正常只含英數字/底線/連字號，理論上不該有路徑分隔符。防禦性擋掉含
 * `/` `\` 的值——沒有這層檢查，`resume-${encId}.json` 這種字串拼接一旦 encId 帶
 * `../../../../tmp/evil` 之類的內容，`path.join(outDir, filename)` 會把 `..` 段正規化掉、
 * 讓寫入路徑跳脫 outDir（已用 node -e 實測驗證過，不是紙上談兵）。伺服器被入侵或契約跑掉時
 * 這是最後一道防線，不依賴信任 server 回應內容。
 */
function safeResumeFilename(encId: string): string {
	if (encId.includes('/') || encId.includes('\\') || encId === '.' || encId === '..') {
		throw new CliError(
			`Unexpected enc_id from server: "${encId}" (contains path separators; refusing to write outside --out)`,
			ExitCode.ServerOrNetworkError
		);
	}
	return `resume-${encId}.json`;
}

/**
 * `resumes export` 核心：list 找出全部 enc_id → 逐份 view 抓聚合 → 各寫一檔
 * `resume-<enc_id>.json`。配額上限 ≤6 份（quota.max_resumes），循序拉取即可，
 * 不需要 `lib/concurrency.ts` 的併發控制；單節失敗語義（create/update 編排的 partial
 * success）在這裡不適用——view 本身不是分節寫入，中途失敗直接讓錯誤上拋（不寫殘缺檔）。
 *
 * `outDir` 要求呼叫端已決議好預設值（`--out` 未帶時的 `process.cwd()` 由
 * `registerPersonalResumesExport` 的 action 處理）——不把 `process.cwd()` 塞進這支函式的
 * 預設參數，方便測試永遠帶明確路徑、不必 mock 全域 `process.cwd`。
 */
export async function runResumesExport(ctx: ResolvedContext, outDir: string): Promise<void> {
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const { resumes } = await fetchResumeList(opts);

	mkdirSync(outDir, { recursive: true });

	for (const item of resumes) {
		// 檔名安全檢查放在發請求「之前」：enc_id 若真的帶跳脫路徑，寧可不浪費這次 view 請求就先擋下來。
		const filePath = join(outDir, safeResumeFilename(item.enc_id));
		const aggregate = await fetchResumeAggregate(opts, item.enc_id);
		writeFileSync(filePath, `${JSON.stringify(aggregate, null, 2)}\n`, 'utf8');
	}

	process.stdout.write(`Exported ${resumes.length} resume(s) to ${outDir}.\n`);
}

export function registerPersonalResumesExport(parent: Command): void {
	parent
		.command('export')
		.description('Export all your resumes as one JSON file per resume')
		.option('--out <dir>', 'destination directory (default: current directory)')
		.action(async (flags: ExportFlags, command: Command) => {
			await runResumesExport(resolveContext(command), flags.out ?? process.cwd());
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { safeResumeFilename };
